import type { NodeLabel, RelationType } from '@wt-codegraph/shared';

/**
 * Per-NodeLabel hex color and pixel size for the Sigma.js canvas.
 * Mirrors the muted palette used by the GitNexus reference UI; sizes encode
 * structural hierarchy (File > Class > Function > Variable).
 */
export const NODE_COLORS: Record<NodeLabel, string> = {
  Repo: '#a855f7',
  Commit: '#7c3aed',
  Directory: '#6366f1',
  Module: '#7c3aed',
  Namespace: '#7c3aed',
  File: '#3b82f6',
  Class: '#f59e0b',
  Interface: '#ec4899',
  Struct: '#f59e0b',
  Enum: '#f97316',
  EnumMember: '#fb923c',
  Function: '#10b981',
  Method: '#14b8a6',
  Constructor: '#10b981',
  Property: '#64748b',
  Field: '#64748b',
  Variable: '#64748b',
  Parameter: '#64748b',
  TypeAlias: '#a78bfa',
  Decorator: '#eab308',
  Annotation: '#eab308',
  Import: '#475569',
  ImportSymbol: '#475569',
  Export: '#475569',
  CallSite: '#94a3b8',
  Reference: '#94a3b8',
  Comment: '#475569',
  Docstring: '#475569',
  Route: '#f43f5e',
  RouteHandler: '#fb7185',
  McpTool: '#a855f7',
  RpcTool: '#a855f7',
  MessageTopic: '#0ea5e9',
  MessagePublisher: '#38bdf8',
  MessageConsumer: '#38bdf8',
  OrmEntity: '#f59e0b',
  OrmRelation: '#fbbf24',
  Process: '#f43f5e',
  ProcessStep: '#fb7185',
  Community: '#818cf8',
  Symbol: '#64748b',
  Section: '#60a5fa',
  ExternalEndpoint: '#a855f7',
};

export const NODE_SIZES: Record<NodeLabel, number> = {
  Repo: 22,
  Commit: 8,
  Directory: 10,
  Module: 13,
  Namespace: 13,
  File: 6,
  Class: 8,
  Interface: 7,
  Struct: 8,
  Enum: 5,
  EnumMember: 2,
  Function: 4,
  Method: 3,
  Constructor: 4,
  Property: 2,
  Field: 2,
  Variable: 2,
  Parameter: 1.5,
  TypeAlias: 3,
  Decorator: 2,
  Annotation: 2,
  Import: 1.5,
  ImportSymbol: 1.5,
  Export: 1.5,
  CallSite: 1,
  Reference: 1,
  Comment: 0,
  Docstring: 0,
  Route: 5,
  RouteHandler: 4,
  McpTool: 5,
  RpcTool: 5,
  MessageTopic: 4,
  MessagePublisher: 3,
  MessageConsumer: 3,
  OrmEntity: 6,
  OrmRelation: 3,
  Process: 0,
  ProcessStep: 0,
  Community: 0,
  Symbol: 2,
  Section: 8,
  ExternalEndpoint: 5,
};

export const COMMUNITY_COLORS: readonly string[] = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#8b5cf6',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
  '#14b8a6',
  '#84cc16',
];

export function getCommunityColor(communityIndex: number): string {
  const palette = COMMUNITY_COLORS;
  return palette[communityIndex % palette.length] ?? palette[0]!;
}

/** Edge type → render color. Falls back to slate when unrecognised. */
export const EDGE_COLORS: Partial<Record<RelationType, string>> = {
  CONTAINS: '#2d5a3d',
  DEFINES: '#0e7490',
  IMPORTS: '#1d4ed8',
  EXPORTS: '#1d4ed8',
  CALLS: '#7c3aed',
  INHERITS_FROM: '#c2410c',
  IMPLEMENTS: '#be185d',
  OVERRIDES: '#c2410c',
  DECORATES: '#854d0e',
  REFERENCES: '#475569',
  USES_TYPE: '#475569',
  PARAMETER_OF: '#475569',
  RETURNS: '#475569',
  PROVIDES: '#0e7490',
  CONSUMES: '#0e7490',
  HANDLES: '#0e7490',
  PUBLISHES: '#0369a1',
  SUBSCRIBES: '#0369a1',
  BELONGS_TO_PROCESS: '#9f1239',
  BELONGS_TO_COMMUNITY: '#4338ca',
  NEXT_STEP: '#9f1239',
};

export const DEFAULT_EDGE_COLOR = '#334155';

/** Labels surfaced in the toggle filter UI (Phase G.2). */
export const FILTERABLE_LABELS: readonly NodeLabel[] = [
  'Directory',
  'File',
  'Class',
  'Interface',
  'Enum',
  'TypeAlias',
  'Function',
  'Method',
  'Variable',
  'Decorator',
  'Import',
  'Route',
  'McpTool',
];

/** Labels that should not render on the canvas — meta nodes only. */
export const HIDDEN_BY_DEFAULT: readonly NodeLabel[] = [
  'Community',
  'Comment',
  'Docstring',
  'Process',
  'ProcessStep',
  'CallSite',
  'Reference',
];
